/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import ReactTooltip from "react-tooltip";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { getAllAppsTags } from "../../../../api/Learnistic";
import { TempCampaignAtom } from "../../../../atoms/CampaignAtom";
import { TagsAtom } from "../../../../atoms/TagsAtom";
import {
  ILearnisticApp,
  ILearnisticTag,
  ITagConditions,
} from "../../../../generated/Api";
import { CampaignValidation } from "../../../../pages/create-campaign";
import { Campaign, INode, INodeTypes } from "../../../../types/CampaignTypes";
import { ITag } from "../../../../types/TagTypes";
import {
  LearnisticAppsToTags,
  LearnistiTagsToApps,
} from "../../../../utils/LearnisticTagConvertions";
import IconBerserkerMail from "../../../Icon";
import LearnisticTagSelect from "../../../LearnisticTagSelect/LearnisticTagSelect";
import { AllIntegrationsAtom } from "../../../Settings/Integrations/Integrations";
import TagSelect from "../../../TagSelect/TagSelect";
import Delay from "./Delay";
import Form, { DefaultFormProps, FormInstance } from "./Form";

const Container = styled.div`
  height: 100%;
  width: 100%;
  padding: 30px;
  display: flex;
  flex-direction: row;
`;

const Row = styled.div`
  height: max-content;

  .css-1h9isgy-TextContainer {
    max-width: 200px !important;
  }
`;

const Title = styled.div`
  color: #2f2f2f;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 5px;
  align-items: center;
  cursor: default;
  display: flex;
`;

const EmailContainer = styled.div`
  height: 100%;
  width: 100%;
  overflow-y: auto;
`;

export function validateInitialTags(initialTags: any[]) {
  if (!initialTags.length) {
    return {
      node: null,
      error: {
        toastInfo: {
          toastText: "Please, select at least one tag",
          toastType: "info",
        },
      },
    };
  }

  return {
    node: {},
    error: null,
  };
}

export function validateStopTags(campagin: Campaign) {
  if (
    !(
      campagin.stopTagConditions === undefined ||
      (Array.isArray(campagin.stopTagConditions) &&
        campagin.stopTagConditions.length)
    )
  ) {
    return {
      node: null,
      error: {
        toastInfo: {
          toastText: "Please, select at least one tag",
          toastType: "info",
        },
      },
    };
  }

  return {
    node: {},
    error: null,
  };
}

export function validateManageTags(node: INode): CampaignValidation {
  const addedTags = node.manageTag?.addedTags;
  const removedTags = node.manageTag?.removedTags;
  const removedLearnisticTags = node.manageTag?.removedLearnisticTags;
  const addedLearnisticTags = node.manageTag?.addedLearnisticTags;
  if (
    addedTags?.length ||
    removedTags?.length ||
    removedLearnisticTags?.length ||
    addedLearnisticTags?.length
  ) {
    // @ts-ignore
    const newAction: INode = {
      _id: node._id,
      name: "",
      type: INodeTypes.ManageTag,
      manageTag: {
        addedTags: addedTags || [],
        removedTags: removedTags || [],
        addedLearnisticTags: addedLearnisticTags,
        removedLearnisticTags: removedLearnisticTags,
      },
    };

    return {
      node: newAction,
      error: null,
    };
  } else {
    return {
      node: null,
      error: {
        toastInfo: {
          toastText: "Please, select at least one tag",
          toastType: "info",
        },
      },
    };
  }
}

const ApplyTag = ({
  action,
  nodeIndex,
  initial,
  stopCampaign,
}: DefaultFormProps & {
  initial?: boolean;
  stopCampaign?: boolean;
}): JSX.Element => (
  <Form {...{ action, nodeIndex, initial, stopCampaign }}>
    {({
      updateCampaign,
      updateInitialTags,
      updateStopTags,
      tempCampaign,
    }: FormInstance) => {
      const setTempCampaign = useSetRecoilState(TempCampaignAtom);
      const [loadingLearnisticTags, setLoadingLearnisticTags] =
        useState<boolean>(false);

      const tags = useRecoilValue(TagsAtom);
      const currentNode = tempCampaign?.nodes[nodeIndex];
      const [learnisticApps, setLearnisticApps] = useState<ILearnisticApp[]>(
        []
      );

      let selectedBerserkerAddedTags: ITag[] = [];

      if (initial) {
        selectedBerserkerAddedTags =
          tempCampaign?.tagConditions
            .map((t) => {
              return tags.find((tg) => tg._id === t.tagId) as ITag;
            })
            .filter((tg) => tg) || [];
      } else if (stopCampaign) {
        selectedBerserkerAddedTags =
          (tempCampaign?.stopTagConditions || [])
            .map((t) => {
              return tags.find((tg) => tg._id === t) as ITag;
            })
            .filter((tg) => tg) || [];
      } else {
        selectedBerserkerAddedTags =
          (currentNode?.manageTag?.addedTags || [])
            .map((t) => {
              return tags.find((tg) => tg._id === t._id) as ITag;
            })
            .filter((tg) => tg) || [];
      }

      const selectedBerserkerRemovedTags =
        (currentNode?.manageTag?.removedTags || []).map((t) => {
          return tags.find((tg) => tg._id === t._id) as ITag;
        }) || [];

      let selectedLearnisticAddedTags: ILearnisticTag[] = [];

      if (initial) {
        selectedLearnisticAddedTags =
          LearnisticAppsToTags(
            tempCampaign?.tagConditions
              ?.filter((tagCondition) => tagCondition.learnisticTag)
              .map((tagCondition) => tagCondition.learnisticTag) || [],
            learnisticApps
          ) || [];
      } else if (stopCampaign) {
        selectedLearnisticAddedTags =
          LearnisticAppsToTags(
            (tempCampaign?.stopTagConditions?.filter(
              (tagCondition) => tagCondition && typeof tagCondition !== "string"
            ) as ILearnisticApp[]) || [],
            learnisticApps
          ) || [];
      } else {
        selectedLearnisticAddedTags =
          LearnisticAppsToTags(
            currentNode?.manageTag?.addedLearnisticTags || []
          ) || [];
      }

      const selectedLearnisticRemovedTags: ILearnisticTag[] =
        LearnisticAppsToTags(
          currentNode?.manageTag?.removedLearnisticTags || []
        );

      const integrations = useRecoilValue(AllIntegrationsAtom);

      async function loadLearnisticTags() {
        if (!integrations.find((i) => i.type === "LEARNISTIC")) return;
        const tags = await getAllAppsTags();

        setLearnisticApps(
          tags.map((t) => ({
            ...t,
            tags: t.tags.sort((a, b) => {
              if (a.name < b.name) {
                return -1;
              }
              if (a.name > b.name) {
                return 1;
              }
              return 0;
            }),
          }))
        );
      }

      useEffect(() => {
        loadLearnisticTags();
      }, [integrations]);

      const refreshLearnisticTags = async () => {
        if (loadingLearnisticTags) return;
        try {
          setLoadingLearnisticTags(true);
          await loadLearnisticTags();
          toast("Sucessfully reloaded Learnistic tags", { type: "success" });
        } catch (error) {
          toast("Failed to reload Learnistic tags", { type: "success" });
        } finally {
          setLoadingLearnisticTags(false);
        }
      };

      const hasConditionIntersection = (
        tagsIds: string[],
        tagsPool: string[]
      ) => {
        return !!tagsPool.find((tg) => tagsIds.find((t) => t === tg));
      };

      const hasLearnisticConditionIntersection = (
        tags: ILearnisticTag[],
        tagsPool: ILearnisticTag[]
      ) => {
        return !!tagsPool.find((tg) => tags.find((t) => t === tg));
      };

      const setSelectedBerserkerAddedTags = (addedTags: ITag[]) => {
        if (tempCampaign.campaignOptions.fixed) {
          toast("You cannot modify these campaign's start tags", {
            type: "info",
          });
          return;
        }
        if (initial) {
          if (
            hasConditionIntersection(
              addedTags.map((tag) => tag._id!),
              (tempCampaign.stopTagConditions?.filter(
                (tagCondition) =>
                  tagCondition && typeof tagCondition === "string"
              ) as string[]) || []
            )
          ) {
            toast(
              "You cannot select the same start and stop tag simultaneosly",
              { type: "info" }
            );
            return;
          }
          if (updateInitialTags)
            if (action === "edit") {
              updateInitialTags([
                ...addedTags,
                ...LearnistiTagsToApps(
                  learnisticApps,
                  selectedLearnisticAddedTags
                ),
              ]);
            } else {
              const tagMerge = [
                ...addedTags,
                ...LearnistiTagsToApps(
                  learnisticApps,
                  selectedLearnisticAddedTags
                ),
              ];

              let newTagConditions = tagMerge.map(
                (st: ITag | ILearnisticApp) => {
                  if ((st as any).tags) {
                    return {
                      tagId: "LEARNISTIC",
                      action: "add",
                      learnisticTag: st as ILearnisticApp,
                    } as ITagConditions;
                  } else {
                    return {
                      tagId: (st as ITag)._id,
                      action: "add",
                    } as ITagConditions;
                  }
                }
              );

              setTempCampaign({
                ...tempCampaign,
                tagConditions: newTagConditions,
              });
            }
        } else if (stopCampaign) {
          if (
            hasConditionIntersection(
              addedTags.map((tag) => tag._id!),
              (tempCampaign.tagConditions
                .filter(
                  (tagCondition) => tagCondition && !tagCondition.learnisticTag
                )
                .map((tg) => tg.tagId) as string[]) || []
            )
          ) {
            toast(
              "You cannot select the same start and stop tag simultaneosly",
              { type: "info" }
            );
            return;
          }
          if (updateStopTags)
            if (action === "edit") {
              updateStopTags([
                ...addedTags,
                ...LearnistiTagsToApps(
                  learnisticApps,
                  selectedLearnisticAddedTags
                ),
              ]);
            } else {
              const tagMerge = [
                ...addedTags,
                ...LearnistiTagsToApps(
                  learnisticApps,
                  selectedLearnisticAddedTags
                ),
              ];

              let newStopTagConditions = tagMerge.map(
                (st: ITag | ILearnisticApp) => {
                  if ((st as any).tags) {
                    return st as ILearnisticApp;
                  } else {
                    return (st as ITag)._id;
                  }
                }
              );

              setTempCampaign({
                ...tempCampaign,
                stopTagConditions: newStopTagConditions,
              });
            }
        } else {
          const removedTagsIds = selectedBerserkerRemovedTags.map(
            (tag) => tag._id
          );
          const hasRemovedTags = !!addedTags.find((tag) =>
            removedTagsIds.includes(tag._id)
          );

          if (!hasRemovedTags)
            setTempCampaign({
              ...tempCampaign,
              // @ts-ignore
              nodes: [
                ...tempCampaign.nodes.slice(0, nodeIndex),
                {
                  ...currentNode,
                  manageTag: {
                    ...tempCampaign.nodes[nodeIndex].manageTag,
                    addedTags,
                  },
                },
                ...tempCampaign.nodes.slice(nodeIndex + 1),
              ],
            });
          else
            toast("You cannot add and remove a tag simultaneously", {
              type: "info",
            });
        }
      };

      const setSelectedBerserkerRemovedTags = (removedTags: ITag[]) => {
        const addedTagsIds = selectedBerserkerAddedTags.map((tag) => tag._id);
        const hasAddedTags = !!removedTags.find((tag) =>
          addedTagsIds.includes(tag._id)
        );

        if (!hasAddedTags)
          setTempCampaign({
            ...tempCampaign,
            // @ts-ignore
            nodes: [
              ...tempCampaign.nodes.slice(0, nodeIndex),
              {
                ...currentNode,
                manageTag: {
                  ...tempCampaign.nodes[nodeIndex].manageTag,
                  removedTags,
                },
              },
              ...tempCampaign.nodes.slice(nodeIndex + 1),
            ],
          });
        else
          toast("You cannot add and remove a tag simultaneously", {
            type: "info",
          });
      };

      const setSelectedLearnisticAddedTags = (addedTags: ILearnisticTag[]) => {
        if (tempCampaign.campaignOptions.fixed) {
          toast("You cannot modify these campaign's start tags", {
            type: "info",
          });
          return;
        }
        if (initial) {
          if (
            hasLearnisticConditionIntersection(
              addedTags,
              LearnisticAppsToTags(
                (tempCampaign?.stopTagConditions?.filter(
                  (tagCondition) =>
                    tagCondition && typeof tagCondition !== "string"
                ) as ILearnisticApp[]) || [],
                learnisticApps
              ) || []
            )
          ) {
            toast(
              "You cannot select the same start and stop tag simultaneosly",
              { type: "info" }
            );
            return;
          }

          if (updateInitialTags)
            if (action === "edit") {
              updateInitialTags([
                ...selectedBerserkerAddedTags,
                ...LearnistiTagsToApps(learnisticApps, addedTags),
              ]);
            } else {
              const tagMerge = [
                ...selectedBerserkerAddedTags,
                ...LearnistiTagsToApps(learnisticApps, addedTags),
              ];

              let newTagConditions = tagMerge.map(
                (st: ITag | ILearnisticApp) => {
                  if ((st as any).tags) {
                    return {
                      tagId: "LEARNISTIC",
                      action: "add",
                      learnisticTag: st as ILearnisticApp,
                    } as ITagConditions;
                  } else {
                    return {
                      tagId: (st as ITag)._id,
                      action: "add",
                    } as ITagConditions;
                  }
                }
              );

              setTempCampaign({
                ...tempCampaign,
                tagConditions: newTagConditions,
              });
            }
        } else if (stopCampaign) {
          if (
            hasLearnisticConditionIntersection(
              addedTags,
              LearnisticAppsToTags(
                (tempCampaign?.tagConditions
                  ?.filter(
                    (tagCondition) => tagCondition && tagCondition.learnisticTag
                  )
                  .map((tg) => tg.learnisticTag) as ILearnisticApp[]) || [],
                learnisticApps
              ) || []
            )
          ) {
            toast(
              "You cannot select the same start and stop tag simultaneosly",
              { type: "info" }
            );
            return;
          }
          if (updateStopTags)
            if (action === "edit") {
              updateStopTags([
                ...selectedBerserkerAddedTags,
                ...LearnistiTagsToApps(learnisticApps, addedTags),
              ]);
            } else {
              const tagMerge = [
                ...selectedBerserkerAddedTags,
                ...LearnistiTagsToApps(learnisticApps, addedTags),
              ];

              let newStopTagConditions = tagMerge.map(
                (st: ITag | ILearnisticApp) => {
                  if ((st as any).tags) {
                    return st as ILearnisticApp;
                  } else {
                    return (st as ITag)._id;
                  }
                }
              );

              setTempCampaign({
                ...tempCampaign,
                stopTagConditions: newStopTagConditions,
              });
            }
        } else {
          const removedTagsIds = selectedLearnisticRemovedTags.map(
            (tag) => tag.id
          );
          const hasRemovedTags = !!addedTags.find((tag) =>
            removedTagsIds.includes(tag.id)
          );

          if (!hasRemovedTags)
            setTempCampaign({
              ...tempCampaign,
              // @ts-ignore
              nodes: [
                ...tempCampaign.nodes.slice(0, nodeIndex),
                {
                  ...currentNode,
                  manageTag: {
                    ...tempCampaign.nodes[nodeIndex].manageTag,
                    addedLearnisticTags: LearnistiTagsToApps(
                      learnisticApps,
                      addedTags
                    ),
                  },
                },
                ...tempCampaign.nodes.slice(nodeIndex + 1),
              ],
            });
          else
            toast("You cannot add and remove a tag simultaneously", {
              type: "info",
            });
        }
      };

      const setSelectedLearnisticRemovedTags = (
        removedTags: ILearnisticTag[]
      ) => {
        const addedTagsIds = selectedLearnisticAddedTags.map((tag) => tag.id);
        const hasAddedTags = !!removedTags.find((tag) =>
          addedTagsIds.includes(tag.id)
        );

        if (!hasAddedTags)
          setTempCampaign({
            ...tempCampaign,
            // @ts-ignore
            nodes: [
              ...tempCampaign.nodes.slice(0, nodeIndex),
              {
                ...currentNode,
                manageTag: {
                  ...tempCampaign.nodes[nodeIndex].manageTag,
                  removedLearnisticTags: LearnistiTagsToApps(
                    learnisticApps,
                    removedTags
                  ),
                },
              },
              ...tempCampaign.nodes.slice(nodeIndex + 1),
            ],
          });
        else
          toast("You cannot add and remove a tag simultaneously", {
            type: "info",
          });
      };

      const saveForm = () => {};

      const Component = (
        <EmailContainer>
          {!initial && !stopCampaign && (
            <Delay {...{ action, nodeIndex, tempCampaign }} />
          )}
          <Container>
            <Row
              css={css`
                flex: 1;
                display: grid;
                grid-template-columns: 1fr 1fr;
                grid-template-rows: 56px auto auto;
                grid-gap: 32px 16px;
              `}
            >
              {((!initial && !stopCampaign) ||
                !!integrations.find((i) => i.type === "LEARNISTIC")) && (
                <>
                  <div
                    css={css`
                      display: flex;
                      flex-direction: column;
                      justify-content: center;
                      font-size: 14px;
                      color: #707070;
                      text-align: center;
                    `}
                  >
                    <IconBerserkerMail
                      name="berserker-logo"
                      width={40}
                      height={30}
                    />
                    <div style={{ marginTop: "8px" }}>
                      Manage your BerserkerMail tags
                    </div>
                  </div>

                  <div
                    css={css`
                      display: flex;
                      flex-direction: column;
                      justify-content: center;
                      font-size: 14px;
                      color: #707070;
                      text-align: center;
                    `}
                  >
                    {integrations.find((i) => i.type === "LEARNISTIC") && (
                      <>
                        <ReactTooltip
                          id={"learnistic-logo"}
                          place="top"
                          effect="solid"
                          backgroundColor="#333333"
                        >
                          {loadingLearnisticTags
                            ? "Reloading Learnistic tags..."
                            : "Click the logo to refresh the Learnistic tags"}
                        </ReactTooltip>
                        <div
                          data-tip
                          data-for={"learnistic-logo"}
                          onClick={refreshLearnisticTags}
                          css={css`
                            cursor: pointer;
                          `}
                        >
                          <img
                            src={"./icons/learnistic-small.svg"}
                            css={css`
                              height: 30px;
                            `}
                          />
                          <div style={{ marginTop: "8px" }}>
                            Manage your Learnistic tags
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}

              <Row
                css={css`
                  ${(initial || stopCampaign) &&
                  !integrations.find((i) => i.type === "LEARNISTIC")
                    ? css`
                        grid-column: 1 / 3;
                      `
                    : ""}
                `}
              >
                <Title
                  css={css`
                    margin-bottom: 16px;
                  `}
                >
                  <img
                    src={"./icons/tag-small.svg"}
                    css={css`
                      width: 20px;
                      margin-right: 10px;
                    `}
                  />
                  {initial
                    ? "START TAGS"
                    : stopCampaign
                    ? "STOP TAGS"
                    : "APPLY TAGS"}
                </Title>
                <TagSelect
                  tags={selectedBerserkerAddedTags}
                  preserveSystemTags={!initial && !stopCampaign}
                  handleChoseTags={setSelectedBerserkerAddedTags}
                  // If I'm not initial nor stop campaign I can use system tags
                />
              </Row>

              {((!initial && !stopCampaign) ||
                !!integrations.find((i) => i.type === "LEARNISTIC")) && (
                <>
                  <Row>
                    <Row>
                      {integrations.find((i) => i.type === "LEARNISTIC") && (
                        <>
                          <Title
                            css={css`
                              margin-bottom: 16px;
                            `}
                          >
                            <img
                              src={"./icons/tag-small.svg"}
                              css={css`
                                width: 20px;
                                margin-right: 10px;
                              `}
                            />
                            {initial
                              ? "START TAGS"
                              : stopCampaign
                              ? "STOP TAGS"
                              : "APPLY TAGS"}
                          </Title>
                          <LearnisticTagSelect
                            tags={selectedLearnisticAddedTags}
                            tagsPool={learnisticApps}
                            handleChoseTags={setSelectedLearnisticAddedTags}
                          />
                        </>
                      )}
                    </Row>
                  </Row>

                  {!initial && !stopCampaign && (
                    <Row>
                      <Title
                        css={css`
                          margin-bottom: 16px;
                        `}
                      >
                        <img
                          src={"./icons/tag-small.svg"}
                          css={css`
                            width: 20px;
                            margin-right: 10px;
                          `}
                        />
                        REMOVE TAGS
                      </Title>
                      <TagSelect
                        tags={selectedBerserkerRemovedTags as any}
                        preserveSystemTags={!initial && !stopCampaign}
                        handleChoseTags={setSelectedBerserkerRemovedTags}
                      />
                    </Row>
                  )}

                  {!initial && !stopCampaign && (
                    <Row>
                      {integrations.find((i) => i.type === "LEARNISTIC") && (
                        <>
                          <Title
                            css={css`
                              margin-bottom: 16px;
                            `}
                          >
                            <img
                              src={"./icons/tag-small.svg"}
                              css={css`
                                width: 20px;
                                margin-right: 10px;
                              `}
                            />
                            REMOVE TAGS
                          </Title>
                          <LearnisticTagSelect
                            tags={selectedLearnisticRemovedTags}
                            tagsPool={learnisticApps}
                            handleChoseTags={setSelectedLearnisticRemovedTags}
                          />
                        </>
                      )}
                    </Row>
                  )}
                </>
              )}
            </Row>
          </Container>
        </EmailContainer>
      );

      return {
        Component,
        saveForm,
      };
    }}
  </Form>
);

export default ApplyTag;
